/**
 * Native filesystem persistence for Capacitor (Android/iOS).
 *
 * Goal: guarantee Quran audio files survive **app restart**, **device
 * restart** and **storage pressure** on mobile devices, where the WebView
 * Cache Storage can be silently purged by the OS (iOS WKWebView 50 MB
 * cap, Android scoped storage GC).
 *
 * Implementation:
 *   - On native (Capacitor), files are written to ``Directory.Data`` —
 *     the app's private container, persistent until the user uninstalls.
 *   - On web/PWA, ``isNative()`` returns ``false`` and all functions are
 *     no-ops; ``audioStorage.js`` falls back to Cache Storage as before.
 *
 * Path layout::
 *
 *   {Data}/quran-audio/{reciterId}/{riwaya}/{ayahKey-safe}.mp3
 *
 * The metadata layer (IndexedDB) stores a ``localUri`` returned by
 * Filesystem.getUri so playback can use it via ``Capacitor.convertFileSrc``.
 */
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';

const ROOT = 'quran-audio';

export function isNative() {
  try {
    return !!Capacitor?.isNativePlatform?.();
  } catch {
    return false;
  }
}

function safe(s) {
  return String(s || '').replace(/[^\w-]/g, '_');
}

function pathFor({ ayahKey, reciterId, riwaya }) {
  return `${ROOT}/${safe(reciterId)}/${safe(riwaya)}/${safe(ayahKey)}.mp3`;
}

/* ---------- IO helpers ---------- */

async function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      // strip the "data:audio/mpeg;base64," prefix
      const s = String(reader.result || '');
      const idx = s.indexOf(',');
      resolve(idx >= 0 ? s.slice(idx + 1) : s);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function ensureDir(path) {
  // Filesystem.mkdir is recursive when ``recursive: true``.
  try {
    await Filesystem.mkdir({ path, directory: Directory.Data, recursive: true });
  } catch (e) {
    // Already exists is fine.
  }
}

/* ---------- Public API ---------- */

export const nativeFileStorage = {
  isNative,

  /**
   * Write the mp3 blob to the filesystem.
   * Returns the absolute URI usable by ``Capacitor.convertFileSrc`` and a
   * stable ``relPath`` that can be persisted in IndexedDB.
   */
  async writeAudio(file, blob) {
    if (!isNative()) return null;
    const relPath = pathFor(file);
    const dir = relPath.substring(0, relPath.lastIndexOf('/'));
    await ensureDir(dir);
    const data = await blobToBase64(blob);
    await Filesystem.writeFile({
      path: relPath,
      data,
      directory: Directory.Data,
      // Binary file → no Encoding option (raw bytes from base64).
      recursive: true,
    });
    const { uri } = await Filesystem.getUri({ path: relPath, directory: Directory.Data });
    return { uri, relPath, sizeBytes: blob.size };
  },

  /**
   * Resolve a playable URL for the WebView from a stored relPath. Returns
   * null if the file no longer exists on disk (caller should drop the
   * metadata row and fallback to streaming).
   */
  async getPlayableUrl(relPath) {
    if (!isNative() || !relPath) return null;
    try {
      const stat = await Filesystem.stat({ path: relPath, directory: Directory.Data });
      if (!stat) return null;
      const { uri } = await Filesystem.getUri({ path: relPath, directory: Directory.Data });
      return Capacitor.convertFileSrc(uri);
    } catch {
      return null;
    }
  },

  async exists(relPath) {
    if (!isNative() || !relPath) return false;
    try {
      await Filesystem.stat({ path: relPath, directory: Directory.Data });
      return true;
    } catch {
      return false;
    }
  },

  async deleteAudio(relPath) {
    if (!isNative() || !relPath) return;
    try {
      await Filesystem.deleteFile({ path: relPath, directory: Directory.Data });
    } catch {
      /* ignore */
    }
  },

  /**
   * Best-effort cleanup of the whole reciter directory. Called when the
   * user changes reciters or wipes downloads.
   */
  async deleteAll() {
    if (!isNative()) return;
    try {
      await Filesystem.rmdir({ path: ROOT, directory: Directory.Data, recursive: true });
    } catch {
      /* ignore */
    }
  },

  /** Helper used by the audio downloads page. */
  async estimateUsageBytes() {
    if (!isNative()) return null;
    try {
      // No native total dir size API; we walk the tree ourselves.
      let total = 0;
      const stack = [ROOT];
      while (stack.length) {
        const dir = stack.pop();
        let entries = [];
        try {
          const r = await Filesystem.readdir({ path: dir, directory: Directory.Data });
          entries = r.files || [];
        } catch { continue; }
        for (const e of entries) {
          const child = `${dir}/${e.name || e}`;
          if (e.type === 'directory') stack.push(child);
          else {
            try {
              const st = await Filesystem.stat({ path: child, directory: Directory.Data });
              total += Number(st.size || 0);
            } catch { /* ignore */ }
          }
        }
      }
      return total;
    } catch {
      return null;
    }
  },
};

export const NATIVE_FS_AVAILABLE = isNative();
